/**
 * The API client.
 *
 * The base path is injected at build time from BASE_PATH so the same source
 * works at the root of a host and under a subpath such as /journal. Nothing
 * here hardcodes a host, a port or a product name.
 *
 * The shapes below are the contract in docs/developer/contracts.md. Change
 * them there first.
 */

export const API_BASE: string = import.meta.env.VITE_API_BASE ?? '/api'

export interface SeatQueue {
  message: string
  queue_position: number | null
  queue_depth: number | null
  estimated_wait_s: number | null
  seat_limit: number | null
  licence_id: string
}

export class ApiError extends Error {
  status: number
  detail: string
  // A 429 carries the queue position and estimated wait rather than a string,
  // because "all seats are in use" is information the reader needs, not an
  // error to apologise for.
  payload: unknown

  constructor(status: number, detail: string, payload?: unknown) {
    super(detail)
    this.status = status
    this.detail = detail
    this.payload = payload
  }

  get seatQueue(): SeatQueue | null {
    if (this.status !== 429) return null
    const detail = (this.payload as { detail?: SeatQueue })?.detail
    return detail && typeof detail === 'object' ? detail : null
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    // Session cookies are httpOnly; the browser sends them, the script cannot
    // read them.
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      ...(init.headers ?? {}),
    },
  })

  if (response.status === 204) {
    return undefined as T
  }

  const text = await response.text()
  const payload = text ? JSON.parse(text) : null

  if (!response.ok) {
    const detail =
      typeof payload?.detail === 'string'
        ? payload.detail
        : (payload?.detail?.message ?? `Request failed with status ${response.status}`)
    throw new ApiError(response.status, detail, payload)
  }

  return payload as T
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
}

/** Build a query string, dropping empty values. */
export function qs(params: Record<string, string | number | boolean | null | undefined>): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue
    search.set(key, String(value))
  }
  const text = search.toString()
  return text ? `?${text}` : ''
}

// --- shapes -----------------------------------------------------------------

export interface ClientConfig {
  product_name: string
  base_path: string
  environment: string
  support_email: string
  features: Record<string, boolean>
}

/** One region of the landing page's sample, in fractions of the page. */
export interface LandingRegion {
  type: string
  x: number
  y: number
  w: number
  h: number
  selected: boolean
}

/**
 * What the archive actually holds. Every figure is counted rather than
 * asserted, and the sample page appears only where a publisher nominated one.
 */
export interface LandingFacts {
  publication: string | null
  place: string | null
  institution: string | null
  year_range: string | null
  editions: number
  pages: number
  articles: number
  sample: {
    page_label: string
    date: string
    geometry: string
    image_url: string
    regions: LandingRegion[]
    address: [string, string][]
  } | null
}

export interface Institution {
  id: string
  name: string
  slug: string
  kind: string
}

export interface Me {
  id: string
  email: string
  display_name: string
  status: string
  orcid: string | null
  institution: Institution | null
  roles: string[]
  permissions: string[]
  features: Record<string, boolean>
}

export interface Anonymous {
  authenticated: false
  permissions: string[]
  features: Record<string, boolean>
}

export type Viewer = Me | Anonymous

export function isSignedIn(viewer: Viewer | undefined): viewer is Me {
  return !!viewer && !('authenticated' in viewer)
}

export interface UserRow {
  id: string
  email: string
  display_name: string
  status: string
  auth_provider: string
  institution_id: string | null
  last_login_at: string | null
  created_at: string
  roles: string[]
}

export interface RoleRow {
  id: string
  key: string
  name: string
  description: string | null
  is_builtin: boolean
  is_anonymous_default: boolean
  permissions: string[]
  user_count: number
}

export interface PermissionRow {
  id: string
  key: string
  category: string
  description: string | null
}

export interface FeatureFlagRow {
  id: string
  key: string
  name: string
  description: string | null
  enabled: boolean
  rollout: Record<string, unknown>
  is_ai_feature: boolean
  updated_at: string
}

/** A SUSHI key as issued: shown once, never stored in the clear. */
export interface SushiKey {
  institution_id: string
  customer_id: string
  requestor_id: string
  api_key: string
  endpoint: string
}

export interface LicenceRow {
  id: string
  publication_id: string
  institution_id: string
  publication_name: string | null
  institution_name: string | null
  access_model: string
  concurrent_limit: number | null
  named_seat_limit: number | null
  session_idle_timeout_s: number | null
  session_max_s: number | null
  permits_download: boolean
  permits_export: boolean
  is_trial: boolean
  starts_on: string | null
  ends_on: string | null
  active: boolean
}

export interface UtilisationRow {
  licence_id: string
  access_model: string
  seat_limit: number | null
  seats_in_use: number
  queue_depth: number
  peak_today: number
  peak_all_time: number
  sessions_today: number
}

export interface ContentSessionRow {
  id: string
  user_id: string
  licence_id: string
  started_at: string
  last_seen_at: string
  ip: string | null
  user_agent: string | null
  user_email: string | null
  idle_seconds: number | null
}

export interface QueueEntryRow {
  id: string
  licence_id: string
  user_id: string
  user_email: string | null
  status: string
  requested_at: string
  offered_at: string | null
  position: number | null
}

export interface AcquisitionRow {
  status: 'active' | 'queued' | 'denied'
  licence_id: string
  session_id: string | null
  message: string
  reason: string | null
  queue_position: number | null
  queue_depth: number | null
  estimated_wait_s: number | null
  seats_in_use: number | null
  seat_limit: number | null
}

export interface EditionRow {
  id: string
  publication_id: string
  pub_date: string | null
  edition_label: string | null
  page_count: number | null
  source_type: string
  route: string | null
  probe_score: number | null
  ingest_status: string
  sha256: string
  source_filename: string | null
  page_w_pt: number | null
  page_h_pt: number | null
  column_count: number | null
  created_at: string
}

export interface StagePlanRow {
  stage: string
  implemented: boolean
  status: string
  attempt: number
  duration_ms: number | null
  message: string | null
  metrics: Record<string, unknown>
}

export interface AuditRow {
  id: string
  created_at: string
  actor_id: string | null
  actor_email: string | null
  action: string
  object_type: string | null
  object_id: string | null
  outcome: string
  counter_metric: string | null
  ip: string | null
  metadata: Record<string, unknown>
}

// --- content ----------------------------------------------------------------

/** [x0, y0, x1, y1] in the page's raster pixels, origin top-left. */
export type Region = [number, number, number, number]

/** One word and where it sits: [x0, y0, x1, y1, text, confidence]. */
export type WordBox = [number, number, number, number, string, number]

export type UnitType = 'article' | 'photograph' | 'advertisement' | 'notice' | 'listing'

export interface BlockRow {
  id: string
  bbox: Region
  block_type: string
  reading_order: number
  column_index: number | null
  confidence: number | null
  source_engine: string
  raw_text: string | null
  corrected_text: string | null
  font_meta: Record<string, unknown> | null
  article_id?: string | null
  unit_id?: string | null
  page_id?: string | null
  sequence?: number | null
  /** Present only when the page was requested with ?words=true. */
  words?: WordBox[] | null
}

/** A searchable thing of any type, as a result card shows it. */
export interface SearchUnitResult {
  id: string
  unit_type: UnitType
  object_id: string
  headline: string | null
  deck: string | null
  byline: string | null
  pub_date: string | null
  section: string | null
  section_label: string | null
  page_number: number | null
  page_id: string | null
  edition_id: string
  route: 'A' | 'B' | null
  engine: string | null
  word_count: number | null
  columns_label: string | null
  is_wire: boolean
  wire_service: string | null
  confidence: number | null
  snippet: string | null
  rank: number
  region: Region | null
  crop_url: string | null
  restricted: boolean
  /** Found by meaning alone: none of the reader's words appear in it. */
  semantic_only: boolean
  matched_layers: string[]
  /** The page raster's size, so a crop can be described as a fraction of the page. */
  width_px?: number | null
  height_px?: number | null
  thumbnail_url?: string | null
}

export interface SearchPageGroup {
  page_id: string
  edition_id: string
  pub_date: string | null
  page_number: number
  section_label: string | null
  route: 'A' | 'B' | null
  width_px: number | null
  height_px: number | null
  publication_name: string | null
  publication_slug: string | null
  hit_count: number
  best_rank: number
  restricted: boolean
  /** Null when no thumbnail was rasterised; never a URL to a missing image. */
  thumbnail_url?: string | null
  hit_regions: { unit_id: string; unit_type: UnitType; region: Region; rank: number }[]
  units: SearchUnitResult[]
}

export interface SearchLayers {
  exact: number
  /** Stemmed matches: the reader's word shares a stem with a word in the text. */
  stem?: number
  corrected: number
  fuzzy: number
  semantic: number
  phonetic: number
}

export interface SearchResponse {
  query: string
  group: 'page' | 'unit'
  total_units: number
  total_pages: number
  /** Present when group=unit. */
  total?: number
  editions_searched: number
  elapsed_ms: number
  layers: SearchLayers
  limit: number
  offset: number
  pages?: SearchPageGroup[]
  results?: SearchUnitResult[]
}

export type SearchMode = 'blend' | 'exact' | 'stem' | 'fuzzy' | 'semantic' | 'phonetic'
export type SearchSort = 'relevance' | 'oldest' | 'newest'

export interface SearchParams {
  q?: string
  group?: 'page' | 'unit'
  types?: string
  section?: string
  author?: string
  publication?: string
  wire?: 'true' | 'false' | ''
  pipeline?: 'A' | 'B' | ''
  min_confidence?: string
  date_from?: string
  date_to?: string
  mode?: SearchMode
  sort?: SearchSort
  limit?: number
  offset?: number
}

export interface PageBrief {
  id: string
  page_number: number
  section_label: string | null
  width_px: number | null
  height_px: number | null
  ppi: number | null
  thumbnail_url?: string | null
  units?: Partial<Record<UnitType, number>>
  /** Out of view after a rights request: no thumbnail, and the page answers 451. */
  withdrawn?: boolean
}

/** A page taken out of view after a rights request, as the console lists it. */
export interface WithdrawnPage {
  page_id: string
  edition_id: string
  publication_slug: string | null
  publication_name: string | null
  pub_date: string | null
  page_number: number
  section_label: string | null
  withdrawn_at: string | null
  withdrawn_reason: string | null
  withdrawn_by: string | null
}

export interface EditionStats {
  pages?: number
  articles?: number
  photographs?: number
  advertisements?: number
  notices?: number
  listings?: number
  mean_confidence?: number
  words?: number
}

export interface EditionBrief {
  id: string
  pub_date: string | null
  edition_label: string | null
  page_count: number | null
  article_count: number
  publication_name: string | null
  publication_slug: string | null
  source_type: string
  route?: 'A' | 'B' | null
  stats?: EditionStats
}

export interface EditionContentsPage {
  page_id: string
  section_label: string | null
  page_number?: number
  units: { id: string; unit_type: UnitType; title: string | null }[]
}

export interface EditionManifest extends Omit<EditionBrief, 'article_count'> {
  column_count: number | null
  column_pitch_pt: number | null
  page_w_pt?: number | null
  page_h_pt?: number | null
  ingested_at?: string | null
  pages: PageBrief[]
  contents?: EditionContentsPage[]
}

export interface EditionCalendar {
  years: { year: number; editions: number; months: Record<string, string[]> }[]
  /** Runs of years the archive does not hold, inclusive. */
  gaps?: { from: number; to: number; years: number }[]
  earliest?: string | null
  latest?: string | null
}

export interface PageEditionBrief {
  id: string
  pub_date: string | null
  edition_label: string | null
  route: 'A' | 'B' | null
  page_count: number | null
  publication_name: string | null
  publication_slug: string | null
  column_count: number | null
  column_pitch_pt: number | null
  page_w_pt: number | null
  page_h_pt: number | null
}

export interface PageUnit {
  id: string
  unit_type: UnitType
  object_id: string
  title: string | null
  region: Region | null
  block_ids: string[]
  confidence: number | null
}

export interface PageDetail {
  id: string
  edition_id: string
  source_type: string
  page_number: number
  section_label: string | null
  width_px: number | null
  height_px: number | null
  ppi: number | null
  native_ppi: number | null
  coord_space: string
  iiif_id: string | null
  pub_date: string | null
  column_count: number | null
  edition?: PageEditionBrief
  units?: PageUnit[]
  blocks: BlockRow[]
}

export interface PageHit {
  block_id: string
  unit_id: string | null
  unit_type: UnitType | null
  bbox: Region
  text: string
}

export interface PageHits {
  query: string
  hits: PageHit[]
}

export type CitationStyles = Record<'mla' | 'chicago' | 'apa' | 'bibtex' | 'ris', string> &
  Record<string, string | null>

export interface Jump {
  to?: string | null
  from?: string | null
  resolved?: boolean
  continued_article_id?: string | null
}

export interface ArticleDetail {
  edition_id?: string | null
  id: string
  kind?: 'article' | 'notice' | 'listing'
  unit_id?: string | null
  headline: string | null
  deck: string | null
  subhead: string | null
  byline: string | null
  pub_date: string | null
  section: string | null
  page_id: string | null
  page_number: number | null
  section_label: string | null
  word_count: number | null
  is_wire: boolean
  wire_service: string | null
  confidence: number | null
  needs_review: boolean
  raw_text: string | null
  corrected_text: string | null
  correction_source: string | null
  region?: Region | null
  jump?: Jump
  citation: Record<string, string | null>
  blocks: BlockRow[]
}

export interface UnitDetail {
  id: string
  unit_type: UnitType
  object_id: string
  title: string | null
  subtitle: string | null
  byline: string | null
  text_raw: string | null
  text_corrected: string | null
  correction_source: string | null
  confidence: number | null
  region: Region | null
  page_id: string | null
  page_number: number | null
  section_label: string | null
  pub_date: string | null
  edition_id: string
  route: 'A' | 'B' | null
  engine: string | null
  columns_label: string | null
  word_count: number | null
  is_wire: boolean
  wire_service: string | null
  article_id: string | null
  fields: Record<string, unknown>
  blocks: BlockRow[]
  citation: Record<string, string | null>
  permalink: string | null
  jump: Jump
  restricted?: boolean
}

/** Page imagery is served by the application, because object storage cannot
 *  check a licence. These are plain URLs so the browser can cache them. */
/** The original issue, one PDF, as it was delivered. Needs the download right. */
export function editionSourceUrl(editionId: string): string {
  return `${API_BASE}/editions/${editionId}/source.pdf`
}

/** The same file opened for reading in the browser's PDF viewer: the clear
 *  vector rendering, the whole issue as one document. Needs only reading. */
export function editionReadUrl(editionId: string): string {
  return `${API_BASE}/editions/${editionId}/source.pdf?inline=true`
}

export function pageImageUrl(pageId: string, thumb = false): string {
  return `${API_BASE}/pages/${pageId}/image${thumb ? '?thumb=true' : ''}`
}

export function regionUrl(pageId: string, region: string | Region, width = 400): string {
  const text = Array.isArray(region)
    ? `${Math.round(region[0])},${Math.round(region[1])},${Math.round(region[2] - region[0])},${Math.round(region[3] - region[1])}`
    : region
  return `${API_BASE}/pages/${pageId}/region?bbox=${encodeURIComponent(text)}&width=${width}`
}

export function iiifRegion(region: Region): string {
  return `${Math.round(region[0])},${Math.round(region[1])},${Math.round(region[2] - region[0])},${Math.round(region[3] - region[1])}`
}

// --- research ---------------------------------------------------------------

export interface TimelinePoint {
  period: string
  matches: number
  total: number
  share: number
  rate?: number
  route_mix?: { A?: number; B?: number }
  /** True when the period is counted from scanned text and is a floor, not a fact. */
  floor?: boolean
  term?: string
}

export interface Timeline {
  query: string
  interval: string
  points: TimelinePoint[]
  series?: { term: string; points: TimelinePoint[] }[]
}

export interface FacetValue {
  value: string
  label?: string
  count: number
}

export interface Facets {
  query: string
  total: number
  publications: FacetValue[]
  sections: FacetValue[]
  types: FacetValue[]
  authors: FacetValue[]
  origin: { local: number; wire: number }
  pipeline: { A: number; B: number }
  confidence_bands: FacetValue[]
  years: { year: number; count: number; editions: number }[]
  earliest: string | null
  latest: string | null
  licence: { from: string | null; to: string | null; label: string } | null
}

export interface SearchAlternative {
  layer: 'fuzzy' | 'semantic' | 'phonetic'
  query: string
  params: Record<string, string>
  count: number
  finds?: string[]
  note: string
}

export interface SearchAlternatives {
  query: string
  exact: number
  alternatives: SearchAlternative[]
  widen: { label: string; params: Record<string, string>; count: number }[]
}

export interface ClippingRow {
  id: string
  page_id: string
  article_id: string | null
  project_id: string | null
  bbox: Region
  region: string
  note: string | null
  citation: Record<string, string | null>
  page_number: number | null
  created_at: string
  pub_date?: string | null
  section_label?: string | null
  unit_type?: UnitType | null
  headline?: string | null
}

export interface ClippingProject {
  id: string
  name: string
  description: string | null
  clipping_count: number
  created_at: string
  updated_at?: string
}

export interface SavedSearchRow {
  id: string
  name: string
  query: Record<string, unknown>
  alert_enabled: boolean
  alert_frequency?: 'weekly' | 'monthly' | 'off'
  last_alerted_at: string | null
  created_at: string
  result_count?: number | null
  new_count?: number | null
}

export function exportUrl(q: string, fmt: 'csv' | 'json', extra: Record<string, string> = {}): string {
  return `${API_BASE}/search/export${qs({ q, fmt, ...extra })}`
}

// --- review -----------------------------------------------------------------

export type ReviewKind =
  | 'low_confidence_ocr'
  | 'article_segmentation'
  | 'unmatched_advertiser'
  | 'ambiguous_byline'
  | 'routing_uncertain'
  | 'other'

export interface ReviewToken {
  text: string
  confidence: number
  index: number
  flagged: boolean
}

export interface ReviewSuggestion {
  text: string
  source: 'lexicon' | 'raw' | 'llm' | 'editor' | string
  note: string | null
  default?: boolean
}

export interface ReviewItemRow {
  id: string
  kind: ReviewKind
  status: 'open' | 'claimed' | 'resolved' | 'dismissed'
  priority: number
  confidence: number | null
  edition_id: string | null
  pub_date: string | null
  page_id: string | null
  page_label: string | null
  section_label: string | null
  object_type: string
  object_id: string
  block_id: string | null
  region: Region | null
  reading_order: number | null
  engine: string | null
  route: 'A' | 'B' | null
  raw_text: string | null
  corrected_text: string | null
  tokens: ReviewToken[]
  suggestions: ReviewSuggestion[]
  context: string | null
  detail: Record<string, unknown>
}

export interface ReviewQueue {
  total: number
  items: ReviewItemRow[]
  counts: Partial<Record<ReviewKind, number>>
}

export type ReviewDecision = 'accept' | 'correct' | 'skip' | 'illegible' | 'merge' | 'split'

export interface ReviewDecideResponse {
  item: ReviewItemRow
  next: ReviewItemRow | null
}

export interface ReviewStats {
  session: {
    reviewed: number
    accepted: number
    corrected: number
    skipped: number
    illegible: number
    mean_seconds: number | null
    rate_per_min: number | null
    streak: number
  }
  today: { done: number; target: number }
  throughput: { date: string; count: number }[]
  composition: { label: string; count: number }[]
}

// --- administration ---------------------------------------------------------

export interface SearchTuning {
  weights: Record<string, number>
  fuzzy: { trigram_threshold: number; max_edit_distance: number; phonetic_fallback: boolean; min_token_length: number }
  semantic: { k: number; min_cosine: number; blend: number }
}

export interface UsageDay {
  date: string
  sessions: number
  peak_concurrency: number
  denials: number
  mean_session_s: number | null
  searches: number
  page_views: number
}

export interface UsageReport {
  licence_id: string | null
  from: string
  to: string
  days: UsageDay[]
  totals: Omit<UsageDay, 'date'> & { days_at_limit?: number }
}
